import {
  ETIQUETA_HITO,
  ETIQUETA_HITO_EN,
  HITOS_DE_CONTENEDOR,
  type HitoDeContenedor,
  type ResultadoDeCambioDeFechaDeContenedores,
} from '@cnc/contracts';
import {useState} from 'react';
import {useCambiarFechaDeContenedores} from '../api/seguimiento';
import {et, tr} from '../i18n/idioma';
import {fecha as formatoFecha, numero} from '../utilidades/formato';

// Corregir una fecha de varios contenedores a la vez (Daniel, 23/10/2026):
// la entrada a puerto, el zarpe u otro hito. Primero se revisa que cambiaria;
// lo guardado queda corregido en la app y una nueva carga de Seguimiento
// Concentrados no lo pisa.
export function CambioDeFechaDeContenedores({
  contenedores,
  hitoInicial = 'entrada_puerto',
  onListo,
}: {
  contenedores: Array<{exportacion: string; contenedor: string}>;
  hitoInicial?: HitoDeContenedor;
  onListo: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [hito, setHito] = useState<HitoDeContenedor>(hitoInicial);
  const [fecha, setFecha] = useState('');
  const [resultado, setResultado] = useState<ResultadoDeCambioDeFechaDeContenedores | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cambiar = useCambiarFechaDeContenedores();
  const hitos = et(ETIQUETA_HITO, ETIQUETA_HITO_EN);

  const ejecutar = (modo: 'revisar' | 'aplicar') => {
    setError(null);
    cambiar.mutate(
      {contenedores, hito, fecha: fecha || null, modo},
      {
        onSuccess: r => {
          setResultado(r);
          if (r.aplicado) onListo();
        },
        onError: e => setError(e.message),
      },
    );
  };

  return (
    <>
      <button
        type="button"
        disabled={!contenedores.length}
        onClick={() => {
          setAbierto(v => !v);
          setResultado(null);
        }}
        className="rounded-md border border-linea bg-white px-2 py-0.5 hover:bg-papel disabled:opacity-40"
      >
        ⇶ {tr('Cambiar fecha de los contenedores', 'Change container date')}
        {contenedores.length ? ` (${numero(contenedores.length)})` : ''}
      </button>
      {abierto && contenedores.length > 0 && (
        <div
          className="flex basis-full flex-col gap-2 rounded-md border border-linea bg-white px-3 py-2"
          role="group"
          aria-label={tr('Cambiar fecha de los contenedores', 'Change container date')}
        >
          <p className="text-apagado">
            {tr(
              'La misma fecha para todos los contenedores marcados. Queda corregida en la app: una nueva carga de Seguimiento Concentrados no la cambia. Deja la fecha vacía para borrarla.',
              'The same date for all ticked containers. It stays corrected in the app: a new Seguimiento Concentrados upload does not change it. Leave the date empty to clear it.',
            )}
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-apagado">
              {tr('Fecha', 'Date')}
              <select
                value={hito}
                onChange={e => {
                  setHito(e.target.value as HitoDeContenedor);
                  setResultado(null);
                }}
                aria-label={tr('Qué fecha', 'Which date')}
                className="ml-1 rounded-md border border-linea px-2 py-1 text-tinta"
              >
                {HITOS_DE_CONTENEDOR.map(h => (
                  <option key={h} value={h}>
                    {hitos[h]}
                  </option>
                ))}
              </select>
            </label>
            <input
              type="date"
              value={fecha}
              onChange={e => {
                setFecha(e.target.value);
                setResultado(null);
              }}
              aria-label={tr('Fecha nueva', 'New date')}
              className="rounded-md border border-linea px-2 py-1 text-tinta"
            />
            <button
              type="button"
              onClick={() => ejecutar('revisar')}
              disabled={cambiar.isPending}
              className="rounded-md border border-verde bg-white px-3 py-1 text-verde-oscuro hover:bg-verde-claro disabled:opacity-50"
            >
              {tr('Revisar qué cambiaría', 'Review what would change')}
            </button>
            <button
              type="button"
              onClick={() => ejecutar('aplicar')}
              disabled={cambiar.isPending || !resultado || resultado.aplicado || resultado.cambiados === 0}
              title={!resultado ? tr('Primero revisa qué cambiaría', 'First review what would change') : ''}
              className="rounded-md bg-verde px-3 py-1 font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {cambiar.isPending ? tr('Trabajando…', 'Working…') : tr('Aplicar', 'Apply')}
            </button>
            {error && <span className="text-alerta">{error}</span>}
          </div>
          {resultado && (
            <div>
              <p className={resultado.aplicado ? 'text-verde-oscuro' : 'text-apagado'}>
                {resultado.aplicado
                  ? tr(
                      `${numero(resultado.cambiados)} contenedores corregidos.`,
                      `${numero(resultado.cambiados)} containers corrected.`,
                    )
                  : tr(
                      `Cambiarían ${numero(resultado.cambiados)} contenedores.`,
                      `${numero(resultado.cambiados)} containers would change.`,
                    )}
              </p>
              <ul className="mt-1 max-h-40 overflow-auto">
                {resultado.filas.map(f => (
                  <li key={`${f.exportacion}|${f.contenedor}`} className={f.error ? 'text-apagado' : ''}>
                    {f.exportacion} · {f.contenedor}: {f.antes ? formatoFecha(f.antes) : '—'} →{' '}
                    {f.despues ? formatoFecha(f.despues) : '—'}
                    {f.error && <span className="ml-1 text-alerta">({f.error})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  );
}
